import { useTranslation } from 'react-i18next';
import { Box, Divider, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import addPointsIcon from '../assets/icons/basket-plus.svg';
import backPointsIcon from '../assets/icons/money-back.svg';
import spendingPointsIcon from '../assets/icons/wallet.svg';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import SvgIcon from '../components/SvgIcon';
import { formattedDate } from '../utils';
import { IIndividualPointResult, PointType } from '../utils/types';

type PointDetailDialogProps = {
  open: boolean;
  point: IIndividualPointResult;
  onClose: () => void;
};

export const PointDetailDialog = ({
  open,
  point,
  onClose,
}: PointDetailDialogProps) => {
  const { t } = useTranslation();

  const getPointTypeCard = (pointType?: PointType) => {
    let icon = addPointsIcon;
    let text = t('pointsTableMobile.credit');

    if (pointType === 'DEBIT') {
      icon = spendingPointsIcon;
      text = t('pointsTableMobile.debit');
    }
    if (pointType === 'CREDITREFUND') {
      icon = backPointsIcon;
      text = t('pointsTableMobile.creditRefund');
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 3,
          mb: 3,
        }}
      >
        <SvgIcon
          src={icon}
          style={{
            fill: '#000000',
            width: 30,
            height: 30,
            display: 'flex',
            justifyContent: 'center',
          }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ fontSize: '14px', fontWeight: 'bold', color: 'inherit' }}>
            {text}
          </Box>
          <Box
            color="#677284"
            sx={{ font: 'normal normal normal 16px Config' }}
          >
            {formattedDate(point.transactionDate)}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <Box sx={{ display: 'flex' }}>
          {t('pointDetailDialog.title')}
          <Button
            style={{ marginLeft: 'auto' }}
            onClick={onClose}
            startIcon={<CloseIcon />}
          />
        </Box>
        <Divider
          sx={{
            borderColor: '#F4F4F4',
          }}
        />
      </DialogTitle>
      <DialogContent>
        <Box>
          {getPointTypeCard(point.pointType)}
          <Box>{point.customer}</Box>
          {point.orderNumber && (
            <PointDetailDialogRow
              title={t('pointDetailDialog.orderNumber')}
              content={point.orderNumber}
            />
          )}
          {point.material && <PointDetailDialogRow content={point.material} />}
          {point.startDate && (
            <PointDetailDialogRow
              title={t('pointDetailDialog.startDate')}
              content={formattedDate(point.startDate)}
              marginTop={3}
            />
          )}
          {point.endDate && (
            <PointDetailDialogRow
              title={t('pointDetailDialog.endDate')}
              content={formattedDate(point.endDate)}
            />
          )}
          {point.status && (
            <PointDetailDialogRow
              marginTop={3}
              title={t('pointDetailDialog.state')}
              content={t(`pointStatus.${point.status}`)}
            />
          )}
          <Divider
            sx={{
              borderColor: '#F4F4F4',
            }}
          />
          <PointDetailDialogRow
            title={t('pointDetailDialog.point')}
            content={point.point.toString()}
            color="secondary"
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

type PointDetailDialogRowProps = {
  title?: string;
  content: string;
  marginTop?: number | string;
  color?: string;
};

function PointDetailDialogRow({
  title,
  content,
  marginTop,
  color,
}: PointDetailDialogRowProps) {
  return (
    <Box mt={marginTop}>
      {title && (
        <Box
          mr={2}
          color={color || '#677284'}
          sx={{ font: 'normal normal 600 16px Config;' }}
        >
          {title}:
        </Box>
      )}
      <Box
        color={color || '#677284'}
        sx={{ font: 'normal normal normal 16px Config' }}
      >
        {content}
      </Box>
    </Box>
  );
}
